import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { getProject, fetchTicketComments } from "../../../services/projects";
import { refetchIntervalUnlessMfaStepParked } from "../../../utils/enterpriseMfaStep";
import { buildCitableContextFiles } from "../chatUtils";

/** Query timing for the open ticket viewer. Only poll while a review is in flight. */
export function ticketViewQueryTiming(pollingEnabled: boolean): {
  staleTime: number;
  refetchInterval: number | false;
} {
  return {
    staleTime: pollingEnabled ? 0 : 60_000,
    refetchInterval: pollingEnabled ? 5_000 : false,
  };
}

export function useTicketViewData(
  ticketId: string | null,
  options?: { pollingEnabled?: boolean },
) {
  const { staleTime, refetchInterval } = ticketViewQueryTiming(
    options?.pollingEnabled ?? false,
  );

  const ticket = useQuery({
    queryKey: ["ticket-view", ticketId],
    queryFn: () => getProject(ticketId!),
    enabled: !!ticketId,
    staleTime,
    // A parked MFA step stops the poll: every tick would re-read the same step
    // and re-dispatch `openEnterpriseMfaSwitch`, re-opening the dialog the user
    // is typing their code into. The park clears with the step, and the next
    // render resumes polling into the resource the step was gating.
    refetchInterval: (query) =>
      refetchIntervalUnlessMfaStepParked(query.state.error, refetchInterval),
  });

  const comments = useQuery({
    queryKey: ["ticket-view-comments", ticketId],
    queryFn: () => fetchTicketComments({ ticketId: ticketId!, query: "" }),
    enabled: !!ticketId,
    staleTime,
    refetchInterval: (query) =>
      refetchIntervalUnlessMfaStepParked(query.state.error, refetchInterval),
  });

  const ticketData = ticket.data;
  const latestBoard = ticketData?.revisionBoards
    ?.filter((b) => !b.deletedAt)
    ?.at(-1);

  // Stabilize array identities across poll cycles. Without this, consumers
  // see a new array reference on every refetch which triggers effects
  // (e.g. scroll-reset in ImageViewer) even when the underlying files
  // haven't changed.
  const boardContextFiles = useMemo(
    () => latestBoard?.contextFiles ?? [],
    [latestBoard?.contextFiles],
  );
  // Stabilize reviewFiles identity across poll cycles (same reason as
  // boardContextFiles above).
  const latestReviewFiles = useMemo(
    () => latestBoard?.reviewFiles ?? [],
    [latestBoard?.reviewFiles],
  );
  const commentsData = comments.data;
  // The context pane resolves comment citations against this list, so it must
  // also carry cited documents that are not board context files: the reviewed
  // document itself (self-citations) and shared enterprise docs (guidelines).
  const contextFiles = useMemo(
    () =>
      buildCitableContextFiles(
        boardContextFiles,
        latestReviewFiles,
        commentsData,
      ),
    [boardContextFiles, latestReviewFiles, commentsData],
  );
  const hasContextAssets = contextFiles.length > 0;

  return {
    ticket: ticketData,
    comments: comments.data,
    isLoading: ticket.isLoading || comments.isLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  };
}
